import { fetchEventSource } from "@microsoft/fetch-event-source";

import ApiRoutes from "../config/api.routes";
import AxiosFactory from "./http.service";
import AuthService from "./auth.service";
import { consoleError } from "../utils/methods";
import { Notification, NotificationListResponse } from "../components/Notifications/types";

// REST client for the notification feed. Mirrors auth.service.ts: every call
// builds a fresh AxiosFactory (which injects the Authorization header from
// AuthService) and swallows failures via consoleError so callers can decide how
// to react (the context reverts optimistic updates on a falsey/throwing result).

interface ListParams {
  page?: number;
  limit?: number;
  unreadOnly?: boolean;
}

const fetchNotifications = async ({
  page = 1,
  limit = 20,
  unreadOnly = false,
}: ListParams = {}): Promise<NotificationListResponse | null> => {
  try {
    const { axios } = new AxiosFactory();
    const res = await axios.get(ApiRoutes.notifications.list, {
      params: { page, limit, unreadOnly },
    });
    return res.data as NotificationListResponse;
  } catch (error: any) {
    consoleError("Failed to fetch notifications", { error });
    return null;
  }
};

const fetchUnreadCount = async (): Promise<number | null> => {
  try {
    const { axios } = new AxiosFactory();
    const res = await axios.get(ApiRoutes.notifications.count);
    return Number(res.data?.unread ?? 0);
  } catch (error: any) {
    consoleError("Failed to fetch unread notification count", { error });
    return null;
  }
};

const markAsRead = async (id: string): Promise<boolean> => {
  try {
    const { axios } = new AxiosFactory();
    await axios.patch(ApiRoutes.notifications.read(id));
    return true;
  } catch (error: any) {
    consoleError("Failed to mark notification as read", { id, error });
    return false;
  }
};

const markAllAsRead = async (): Promise<boolean> => {
  try {
    const { axios } = new AxiosFactory();
    await axios.patch(ApiRoutes.notifications.readAll);
    return true;
  } catch (error: any) {
    consoleError("Failed to mark all notifications as read", { error });
    return false;
  }
};

const dismiss = async (id: string): Promise<boolean> => {
  try {
    const { axios } = new AxiosFactory();
    await axios.delete(ApiRoutes.notifications.dismiss(id));
    return true;
  } catch (error: any) {
    consoleError("Failed to dismiss notification", { id, error });
    return false;
  }
};

const dismissAll = async (): Promise<boolean> => {
  try {
    const { axios } = new AxiosFactory();
    await axios.delete(ApiRoutes.notifications.dismissAll);
    return true;
  } catch (error: any) {
    consoleError("Failed to dismiss all notifications", { error });
    return false;
  }
};

interface SubscribeHandlers {
  /** Fired for each pushed Notification object. */
  onNotification: (n: Notification) => void;
  /** Fired when the stream opens (or re-opens after a retry). */
  onOpen?: () => void;
  /** Fired when the stream errors fatally (auth missing / aborted). */
  onError?: (error: unknown) => void;
}

// Opens the SSE stream and pushes each Notification to onNotification.
// Uses fetchEventSource (not native EventSource) because the API authenticates
// via the Authorization header, which EventSource cannot send. The library
// reconnects automatically on transient errors; we only abort on auth loss.
// Returns a disconnect function — always call it on unmount / logout.
const subscribe = ({ onNotification, onOpen, onError }: SubscribeHandlers): (() => void) => {
  const token = AuthService.getToken();
  if (!token) {
    consoleError("Cannot open notification stream: no auth token");
    return () => {};
  }

  const controller = new AbortController();
  const url = `${process.env.REACT_APP_API_PATH}${ApiRoutes.notifications.stream}`;

  fetchEventSource(url, {
    signal: controller.signal,
    headers: {
      Authorization: token,
      Accept: "text/event-stream",
    },
    // Keep the stream alive when the tab is backgrounded; the page-visibility
    // pause that fetchEventSource applies by default would otherwise drop pushes.
    openWhenHidden: true,
    onopen: async () => {
      onOpen?.();
    },
    onmessage(event) {
      // Heartbeat / comment frames have no data — ignore them.
      if (!event.data) return;
      try {
        onNotification(JSON.parse(event.data) as Notification);
      } catch {
        // Non-JSON frame (heartbeat) — ignore.
      }
    },
    onerror(error) {
      // Returning lets the library retry with backoff. Surface it so callers can
      // fall back to polling if the stream never establishes.
      consoleError("Notification stream error (will retry)", { error });
      onError?.(error);
    },
  }).catch((error) => {
    // Thrown only when the request is aborted or fails fatally.
    if (!controller.signal.aborted) {
      consoleError("Notification stream closed", { error });
      onError?.(error);
    }
  });

  return () => controller.abort();
};

const NotificationService = {
  fetchNotifications,
  fetchUnreadCount,
  markAsRead,
  markAllAsRead,
  dismiss,
  dismissAll,
  subscribe,
};

export default NotificationService;
